import { QUERY_RESPONSE_RECEIVED } from '@web-platform/shared-universal-constants/src/network';
import { SEARCH_PARAMS_CHANGED } from '@web-platform/shared-universal-constants/src/userSearch';
import { MICHELIN_GET_PACKAGES_REAL_TIME_SAVINGS } from '@web-platform/shared-universal-types/src/network/requestTypes';

import { USER_REQUESTED_SEARCH } from '../../../../actions/searchControls';

import type { SkyscannerMichelinRealtimesavingsV1RealTimeSavings } from '@skyscanner-internal/michelin-web-http-client';
import type { Action } from '@web-platform/shared-universal-types/src/redux/banana/actions/actions-banana-types';
import type {
  PackagesRealTimeSavingsState,
  PackagesRealTimeSavingsOffer,
} from '@web-platform/shared-universal-types/src/redux/state/shared/michelin/packagesRealTimeSavings';

export const INITIAL_STATE: PackagesRealTimeSavingsState = {
  offers: [],
  nightsStay: 0,
  packagesSearchUri: '',
  hasLoaded: false,
};

const transformOffer = (
  offer: SkyscannerMichelinRealtimesavingsV1RealTimeSavings.RealTimeSavingsOffer,
): PackagesRealTimeSavingsOffer | null => {
  const { quote } = offer;
  if (!quote) return null;

  return {
    hotelId: quote.id ?? '',
    hotelName: quote.name ?? '',
    hotelImage: quote.imageUri ?? '',
    providerName: quote.partnerName ?? '',
    providerLogo: quote.partnerLogo ?? '',
    price: Number(quote.price?.amount ?? 0),
    redirectUrl: quote.packageUri ?? '',
    neighborhood: quote.neighbourhood ?? quote.city ?? '',
    starRating: quote.stars ?? 0,
    rating: {
      score: quote.rating?.score ?? 0,
      count: quote.rating?.count ?? 0,
    },
    savingPrice: Number(offer.realTimeSavings?.amount ?? 0),
    hotelPrice: Number(offer.hotelPrice?.amount ?? 0),
    websiteId: offer.websiteId ?? '',
    boardType: quote.boardType ?? '',
  };
};

const transformPackagesRealTimeSavings = (
  response: SkyscannerMichelinRealtimesavingsV1RealTimeSavings.RealTimeSavingsResponse,
): Omit<PackagesRealTimeSavingsState, 'hasLoaded'> => {
  if (!response.offers) return INITIAL_STATE;
  if (!response.nightsStay) return INITIAL_STATE;
  if (!response.packagesSearchUri) return INITIAL_STATE;

  const offers = response.offers
    .map(transformOffer)
    .filter((offer): offer is PackagesRealTimeSavingsOffer => offer !== null);

  if (offers.length === 0) return INITIAL_STATE;

  return {
    offers,
    nightsStay: response.nightsStay,
    packagesSearchUri: response.packagesSearchUri,
  };
};

const packagesRealTimeSavingsReducer = (
  state: PackagesRealTimeSavingsState = INITIAL_STATE,
  action: Action,
): PackagesRealTimeSavingsState => {
  switch (action.type) {
    case QUERY_RESPONSE_RECEIVED: {
      const { payload } = action;
      if (payload.type === MICHELIN_GET_PACKAGES_REAL_TIME_SAVINGS) {
        if (
          payload.response?.error ||
          !payload.response?.body ||
          typeof payload.response.body !== 'object'
        ) {
          return { ...INITIAL_STATE, hasLoaded: true };
        }
        return { ...transformPackagesRealTimeSavings(payload.response.body), hasLoaded: true };
      }
      return state;
    }

    case USER_REQUESTED_SEARCH: {
      const { payload } = action;
      if (payload.searched) {
        return INITIAL_STATE;
      }
      return state;
    }

    case SEARCH_PARAMS_CHANGED:
      return INITIAL_STATE;

    default:
      return state;
  }
};

export default packagesRealTimeSavingsReducer;

export const get = (state: PackagesRealTimeSavingsState): PackagesRealTimeSavingsState => state;
